// frontend/src/pages/Gallery.jsx
// ✅ FINAL: Plain style badges + Default style filter option

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { useAuth } from "../lib/useAuth";
import { getIdentityHeaders, waitForIdentity, refreshTokenIfNeeded } from '../lib/identityGoogle';
import { trackStyleView } from "../utils/trackStyleView.js";
import {
  API_BASE,
  normalizeListResponse,
  getImageUrl,
  toast,
} from "../lib/api";

const fmt = (d) =>
  new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralBorder: "#E5E7EB",
  shadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
};

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

export default function Gallery() {
  const { user } = useAuth();
  const [items, setItems] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [query, setQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState("newest");
  const [viewer, setViewer] = React.useState(null);
  const [busyId, setBusyId] = React.useState("");
  const [deletingId, setDeletingId] = React.useState("");
  

  // Info banner state
  const [showInfo, setShowInfo] = React.useState(() => {
    try {
      return localStorage.getItem("gallery_info_dismissed") !== "1";
    } catch {
      return true;
    }
  });

  function dismissInfo() {
    setShowInfo(false);
    try {
      localStorage.setItem("gallery_info_dismissed", "1");
    } catch {}
  }

  async function fetchPage(nextCursor) {
    const qs = new URLSearchParams();
    qs.set("limit", "24");
    if (nextCursor) qs.set("cursor", nextCursor);

    const res = await fetch(api(`/api/generations?${qs.toString()}`), {
      credentials: "include",
      headers: { ...getIdentityHeaders() },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.error || res.statusText);

    return normalizeListResponse(json);
  }

  // Track style views when gallery items load
  React.useEffect(() => {
    if (items && items.length > 0) {
      const uniqueStyles = new Set();
      items.forEach(item => {
        const styleSlug = String(item.style || "").toLowerCase().trim();
        if (styleSlug && styleSlug !== "default") {
          uniqueStyles.add(styleSlug);
        }
      });
      
      uniqueStyles.forEach(styleId => {
        trackStyleView(styleId, "gallery");
      });
    }
  }, [items]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        await waitForIdentity();

        const { items: first, nextCursor } = await fetchPage(null);
        if (!alive) return;

        setItems(first);
        setCursor(nextCursor);
      } catch (e) {
        if (alive) {
          setErr(e.message || "Failed to load");
          setItems([]);
          setCursor(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const { items: more, nextCursor } = await fetchPage(cursor);
      setItems((prev) => [...prev, ...more]);
      setCursor(nextCursor);
    } catch (e) {
      setErr(e.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }

  // Get user's custom styles for the info card
  const customStyles = React.useMemo(() => {
    const styles = new Set();
    items.forEach((item) => {
      const s = String(item.style || "default").toLowerCase().trim();
      if (s !== "default" && s !== "") styles.add(s);
    });
    return Array.from(styles);
  }, [items]);

  // Calculate style categories
  const styleCategories = React.useMemo(() => {
    const styleCounts = {};
    
    items.forEach((item) => {
      const styleId = String(item.style || "default").toLowerCase().trim();
      styleCounts[styleId] = (styleCounts[styleId] || 0) + 1;
    });

    const mostUsed = Object.entries(styleCounts)
      .filter(([style]) => style !== "default")
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([style]) => style);

    const recentItems = items.slice(0, 20);
    const recentStylesSet = new Set();
    recentItems.forEach(item => {
      const styleId = String(item.style || "default").toLowerCase().trim();
      if (styleId !== "default") {
        recentStylesSet.add(styleId);
      }
    });
    const recentStyles = Array.from(recentStylesSet);

    return { mostUsed, recentStyles, styleCounts };
  }, [items]);

  // Filter and sort
  const filtered = React.useMemo(() => {
    let list = items.slice();

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (i) =>
          (i.prompt || "").toLowerCase().includes(q) ||
          (i.style || "").toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "newest":
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
        
      case "oldest":
        list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
        
      case "default-only":
        list = list.filter((i) => {
          const styleId = String(i.style || "default").toLowerCase().trim();
          return styleId === "default" || styleId === "";
        });
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
        
      case "most-used":
        if (styleCategories.mostUsed.length > 0) {
          list = list.filter((i) => {
            const styleId = String(i.style || "default").toLowerCase().trim();
            return styleCategories.mostUsed.includes(styleId);
          });
        }
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
        
      case "recent-styles":
        if (styleCategories.recentStyles.length > 0) {
          list = list.filter((i) => {
            const styleId = String(i.style || "default").toLowerCase().trim();
            return styleCategories.recentStyles.includes(styleId);
          });
        }
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;

      case "with-video":
        list = list.filter((i) => i.hasVideo || i.videoUrl);
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;

      default:
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return list;
  }, [items, query, sortBy, styleCategories]);

  // Count animated items for the filter label.
  const videoCount = React.useMemo(
    () => items.filter((i) => i.hasVideo || i.videoUrl).length,
    [items]
  );

  async function toggleAllowOnHome(item) {
    const next = !item.allowOnHome;
    try {
      setBusyId(item.id);
      const res = await fetch(api(`/api/generations/${item.id}/allow-home`), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        credentials: "include",
        body: JSON.stringify({ allow: next }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || res.statusText);

      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, allowOnHome: j.allowOnHome ?? next } : it
        )
      );
      setViewer((v) => (v && v.id === item.id ? { ...v, allowOnHome: j.allowOnHome ?? next } : v));
    } catch (e) {
      toast(e.message || "Failed to update visibility");
    } finally {
      setBusyId("");
    }
  }

  async function deleteItem(id) {
    if (!id) return;
    const yes = window.confirm("Delete this image permanently? This cannot be undone.");
    if (!yes) return;

    try {
      setDeletingId(id);
      const res = await fetch(api(`/api/generations/${id}`), {
        method: "DELETE",
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || res.statusText);

      setItems((prev) => prev.filter((x) => x.id !== id));
      setViewer((v) => (v && v.id === id ? null : v));
      toast("Image deleted");
    } catch (e) {
      toast(e.message || "Failed to delete image");
    } finally {
      setDeletingId("");
    }
  }

  return (
    <Screen>
      <StyleTag />

      <div className="gal-header">
        <div>
          <h1
            className="h1"
            style={{
              background:
                "var(--ab-hero-grad, linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%))",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Your Gallery
          </h1>

          <p className="lead">
            All images <strong>you created</strong> — search, filter, or delete.
            <br />
            <span
              style={{
                fontSize: "0.65em",
                color: "#6B7280",
                display: "block",
                marginTop: 4,
              }}
            >
              Style usage insights are available to creators under{" "}
              <strong>Profile → Manage Styles</strong>.
            </span>
          </p>

          {err ? <div className="err-note">⚠ {err}</div> : null}
        </div>

        <div className="gal-controls">
          <div className="gal-search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by prompt or style…"
            />
          </div>

          <div className="gal-sort">
            <label>
              Sort & Filter
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="default-only">Default style only</option>
                <option value="most-used">
                  Most used styles{styleCategories.mostUsed.length > 0 ? ` (${styleCategories.mostUsed.length})` : " (none)"}
                </option>
                <option value="recent-styles">
                  Recently used styles{styleCategories.recentStyles.length > 0 ? ` (${styleCategories.recentStyles.length})` : " (none)"}
                </option>
                <option value="with-video">
                  🎬 Animated only{videoCount > 0 ? ` (${videoCount})` : ""}
                </option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {showInfo && customStyles.length > 0 && (
        <div className="info-banner">
          <div className="info-banner-icon">ℹ️</div>
          <div className="info-banner-content">
            <h3>Gallery vs. Manage Styles</h3>
            <p>
              <strong>This Gallery</strong> shows all images <strong>you created</strong> (with any style).
            </p>
            <p>
              <strong>Manage Styles</strong> lets you track usage, adjust settings, and view all generations created with your custom styles.
            </p>
            <div className="info-banner-actions">
              <Link to="/styles/manage" className="btn btn-sm">
                Go to Manage Styles →
              </Link>
            </div>
          </div>
          <button className="info-banner-close" onClick={dismissInfo} title="Dismiss">
            ×
          </button>
        </div>
      )}

      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState
          onClear={() => {
            setQuery("");
            setSortBy("newest");
          }}
          hasFilters={query.trim() !== "" || sortBy !== "newest"}
        />
      ) : (
        <>
          {(sortBy !== "newest" || query.trim()) && (
            <div className="filter-info">
              Showing {filtered.length} of {items.length} images
              {sortBy !== "newest" && (
                <span className="filter-badge">
                  {sortBy === "oldest" && "Oldest first"}
                  {sortBy === "default-only" && "Default style only"}
                  {sortBy === "most-used" && "Most used styles"}
                  {sortBy === "recent-styles" && "Recently used styles"}
                  {sortBy === "with-video" && "🎬 Animated only"}
                </span>
              )}
              {query.trim() && (
                <span className="filter-badge">
                  Search: "{query}"
                </span>
              )}
              <button 
                className="clear-filters"
                onClick={() => {
                  setQuery("");
                  setSortBy("newest");
                }}
              >
                Clear all
              </button>
            </div>
          )}

          <div className="masonry">
            {filtered.map((it) => (
              <MasonryCard
                key={it.id}
                item={it}
                user={user}
                busy={busyId === it.id}
                deleting={deletingId === it.id}
                onOpen={() => setViewer(it)}
                onToggle={() => toggleAllowOnHome(it)}
                onDelete={() => deleteItem(it.id)}
              />
            ))}
          </div>

          {cursor && (
            <div className="load-more">
              <button
                className="btn"
                onClick={loadMore}
                disabled={loadingMore}
                title="Load more images"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {viewer && (
        <Lightbox
          item={viewer}
          user={user}
          busy={busyId === viewer.id}
          deleting={deletingId === viewer.id}
          onClose={() => setViewer(null)}
          onToggle={() => toggleAllowOnHome(viewer)}
          onDelete={() => deleteItem(viewer.id)}
        />
      )}
    </Screen>
  );
}

function MasonryCard({ item, user, onOpen, onToggle, onDelete, busy, deleting }) {
  const [loaded, setLoaded] = React.useState(false);
  const url = getImageUrl(item);

  const styleId = React.useMemo(() => {
    const s = String(item.style || "").toLowerCase().trim();
    if (!s || s === "default") return "";
    return s;
  }, [item.style]);

  const onDownload = async (e) => {
    e.stopPropagation();
    try {
      const res = await fetch(api(`/api/generations/${item.id}/download`), {
        method: "GET",
        headers: { ...getIdentityHeaders() },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `artibytes-${item.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch (err) {
      toast(err.message || "Download failed");
    }
  };

  return (
    <article className="masonry-card" onClick={onOpen}>
      <div className="img-wrap">
        <img
          src={url}
          alt={item.prompt || "generation"}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          className={loaded ? "ready" : ""}
          onLoad={() => setLoaded(true)}
        />

        {styleId && (
          <div className="style-chip">
            {styleId}
          </div>
        )}

        {item.allowOnHome && (
          <div className="home-chip" title="Allowed on Home">
            HOME
          </div>
        )}

        {(item.hasVideo || item.videoUrl) && (
          <div className="video-chip" title="Has animated video">
            🎬
          </div>
        )}

        <div className="hover-bar">
          <button className="action" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            View
          </button>

          <button className="action icon" onClick={onDownload} aria-label="Download" title="Download">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 3v10m0 0 4-4m-4 4-4-4M5 21h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            className={`action ${item.allowOnHome ? "on" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            disabled={busy}
            title={item.allowOnHome ? "Remove from Home" : "Allow on Home"}
          >
            {item.allowOnHome ? "On Home" : "Allow Home"}
          </button>

          <button
            className="action danger icon"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            disabled={deleting}
            aria-label="Delete"
            title="Delete"
          >
            {deleting ? "…" : "🗑"}
          </button>
        </div>
      </div>

      <div className="meta">
        <div className="prompt" title={item.prompt}>
          {item.prompt}
        </div>
        <div className="stamp">{fmt(item.createdAt || Date.now())}</div>
      </div>
    </article>
  );
}

function Lightbox({ item, user, onClose, onToggle, onDelete, busy, deleting }) {
  const url = getImageUrl(item);
  
  const styleId = React.useMemo(() => {
    const s = String(item.style || "").toLowerCase().trim();
    if (!s || s === "default") return "";
    return s;
  }, [item.style]);

  // --- Animate (Veo) state ---
  const [pricing, setPricing] = React.useState(null);
  const [credits, setCredits] = React.useState(null);
  const [motionPrompt, setMotionPrompt] = React.useState("");
  const [animState, setAnimState] = React.useState("idle"); // idle | processing | complete | failed
  const [videoUrl, setVideoUrl] = React.useState(null);
  const [animError, setAnimError] = React.useState("");
  const pollRef = React.useRef(null);

  const refreshCredits = React.useCallback(async () => {
    try {
      const res = await fetch(api("/api/credits"), {
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      const j = await res.json();
      if (typeof j.credits === "number") setCredits(j.credits);
    } catch {}
  }, []);

  // Reset animate state whenever the viewed item changes; clear any poll.
  // Also surface an existing video immediately if this item already has one.
  React.useEffect(() => {
    setMotionPrompt("");
    setAnimError("");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const existing = item.videoUrl || item.videoGcsObject;
    if (existing) {
      setAnimState("complete");
      setVideoUrl(api(`/api/animate/video/${item.videoJobId || item.id}`));
    } else {
      setAnimState("idle");
      setVideoUrl(null);
    }
  }, [item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pricing + credits on open; clear poll on unmount.
  React.useEffect(() => {
    (async () => {
      try {
        const res = await fetch(api("/api/animate/pricing"), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        if (res.ok) setPricing(await res.json());
      } catch {}
      await refreshCredits();
    })();
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [refreshCredits]);

  const pollAnimation = React.useCallback((animJobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const startedAt = Date.now();
    const MAX_MS = 6 * 60 * 1000;
    pollRef.current = setInterval(async () => {
      try {
        if (Date.now() - startedAt > MAX_MS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimState("failed");
          setAnimError("Animation timed out. Your credits were refunded.");
          await refreshCredits();
          return;
        }
        const res = await fetch(api(`/api/animate/status/${animJobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await res.json();
        if (j.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setVideoUrl(api(`/api/animate/video/${animJobId}`));
          setAnimState("complete");
        } else if (j.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimState("failed");
          setAnimError(j.error || "Animation failed. Your credits were refunded.");
          await refreshCredits();
        }
      } catch {
        // transient; keep polling
      }
    }, 10000);
  }, [refreshCredits]);

  const handleAnimate = React.useCallback(async () => {
    if (!item?.id) return;
    setAnimError("");

    const tokenValid = await refreshTokenIfNeeded();
    if (!tokenValid) {
      toast("Your session expired. Please sign in again.");
      return;
    }

    setAnimState("processing");
    try {
      const res = await fetch(api("/api/animate"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        body: JSON.stringify({ generationId: item.id, prompt: motionPrompt.trim() || undefined }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || "Failed to start animation");

      if (typeof j.creditsRemaining === "number") setCredits(j.creditsRemaining);
      else await refreshCredits();
      try {
        localStorage.setItem("ab:credits:ping", String(Date.now()));
        window.dispatchEvent(new Event("ab:credits"));
      } catch {}

      pollAnimation(j.jobId);
    } catch (e) {
      setAnimState("failed");
      setAnimError(e.message || "Failed to start animation");
      await refreshCredits();
    }
  }, [item, motionPrompt, pollAnimation, refreshCredits]);

  const onDownload = async () => {
    try {
      const res = await fetch(api(`/api/generations/${item.id}/download`), {
        method: "GET",
        headers: { ...getIdentityHeaders() },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `artibytes-${item.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast(err.message || "Download failed");
    }
  };

  return (
    <div className="lb-backdrop" onClick={onClose}>
      <div className="lb-dialog" onClick={(e) => e.stopPropagation()}>
        {animState === "complete" && videoUrl ? (
          <video
            src={videoUrl}
            controls
            autoPlay
            loop
            playsInline
            style={{ width: "100%", display: "block", background: "#000" }}
          />
        ) : (
          <img src={url} alt={item.prompt} />
        )}
        <div className="lb-info">
          <div className="lb-row">
            {styleId ? (
              <span className="lb-style">{styleId}</span>
            ) : (
              <span className="lb-style">default</span>
            )}
            <span className="lb-date">{fmt(item.createdAt || Date.now())}</span>
          </div>

          <div className="lb-prompt">{item.prompt}</div>

          {/* Animate (Veo) */}
          {pricing?.available !== false && (
            <div className="lb-animate">
              {animState === "complete" && videoUrl ? (
                <div className="lb-anim-hint">🎬 Animated — playing above.</div>
              ) : (
                <>
                  <input
                    className="input"
                    type="text"
                    placeholder="Optional: describe the motion…"
                    value={motionPrompt}
                    onChange={(e) => setMotionPrompt(e.target.value)}
                    disabled={animState === "processing"}
                  />
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleAnimate}
                      disabled={
                        animState === "processing" ||
                        (pricing && typeof credits === "number" && credits < pricing.credits)
                      }
                    >
                      {animState === "processing"
                        ? "Animating…"
                        : `Animate${pricing ? ` (${pricing.credits} credits)` : ""}`}
                    </button>
                    <div className="lb-anim-hint">
                      {pricing ? `${pricing.credits} credits per animation` : "Animation"}
                      {pricing?.durationSeconds ? ` • ~${pricing.durationSeconds}s` : ""}
                      {typeof credits === "number" ? ` • Available: ${credits}` : ""}
                    </div>
                  </div>
                  {animState === "processing" && (
                    <div className="lb-anim-note">
                      Generating — this can take a minute or two.
                    </div>
                  )}
                  {animState === "failed" && animError && (
                    <div className="err-note" style={{ marginTop: 8 }}>{animError}</div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="lb-actions">
            <button className="btn btn-primary" onClick={onDownload}>
              Download
            </button>
            <button
              className="btn"
              onClick={onToggle}
              disabled={busy}
              style={item.allowOnHome ? { borderColor: brand.purple, color: brand.purple } : undefined}
            >
              {item.allowOnHome ? "Remove from Home" : "Allow on Home"}
            </button>
            <button className="btn danger" onClick={onDelete} disabled={deleting} title="Delete permanently">
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onClear, hasFilters }) {
  return (
    <div className="empty">
      <h3>{hasFilters ? "No results" : "No images yet"}</h3>
      <p>
        {hasFilters 
          ? "Try clearing search/filters or generate something new."
          : "Head to Create and generate your first image!"}
      </p>
      <div className="empty-actions">
        {hasFilters && (
          <button className="btn" onClick={onClear}>
            Reset filters
          </button>
        )}
        <a className="btn btn-primary" href="/create">
          Create image
        </a>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="masonry">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="masonry-card">
          <div className="sk-img" />
          <div className="meta">
            <div className="sk-line" />
            <div className="sk-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
.gal-header {
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:24px;
  flex-wrap:wrap;
  margin-bottom: 16px;
}
.gal-controls {
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}
.err-note{
  margin-top:8px;
  color:#B91C1C;
  background:#FEF2F2;
  border:1px solid #FECACA;
  padding:8px 10px;
  border-radius:10px;
  font-size:13px;
}

.filter-info{
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  padding:12px 16px;
  background:linear-gradient(135deg, #F3F0FF 0%, #E7E2FF 100%);
  border:1px solid #D4C5F9;
  border-radius:12px;
  margin-bottom:16px;
  font-size:14px;
  font-weight:600;
  color:#5B4EE8;
}

.filter-badge{
  display:inline-flex;
  align-items:center;
  padding:4px 10px;
  background:#fff;
  border:1px solid #D4C5F9;
  border-radius:999px;
  font-size:12px;
  font-weight:700;
  color:#6D5EF9;
}

.clear-filters{
  margin-left:auto;
  padding:4px 12px;
  background:#fff;
  border:1px solid #D4C5F9;
  border-radius:999px;
  font-size:12px;
  font-weight:700;
  color:#6D5EF9;
  cursor:pointer;
  transition: all 0.2s;
}

.clear-filters:hover{
  background:#6D5EF9;
  color:#fff;
  border-color:#6D5EF9;
}

.info-banner{
  display:flex;
  gap:14px;
  align-items:flex-start;
  background: linear-gradient(135deg, #F3F0FF 0%, #E7E2FF 100%);
  border:1px solid #D4C5F9;
  border-radius:14px;
  padding:16px;
  margin-bottom:20px;
  position:relative;
}
.info-banner-icon{
  font-size:28px;
  flex-shrink:0;
}
.info-banner-content{
  flex:1;
}
.info-banner-content h3{
  margin:0 0 8px 0;
  font-size:16px;
  font-weight:800;
  color:#5B4EE8;
}
.info-banner-content p{
  margin:0 0 8px 0;
  font-size:14px;
  line-height:1.5;
  color:#6D5EF9;
}
.info-banner-content p:last-of-type{
  margin-bottom:12px;
}
.info-banner-actions{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
}
.info-banner-close{
  position:absolute;
  top:12px;
  right:12px;
  width:28px;
  height:28px;
  border-radius:50%;
  border:none;
  background:rgba(109,94,249,0.1);
  color:#5B4EE8;
  font-size:20px;
  font-weight:700;
  cursor:pointer;
  display:grid;
  place-items:center;
  transition: all 0.2s;
}
.info-banner-close:hover{ background:rgba(109,94,249,0.2); }

.btn-sm{
  height:34px;
  padding:0 12px;
  font-size:13px;
  border-radius:10px;
  border:1px solid #6D5EF9;
  background:#fff;
  color:#5B4EE8;
  font-weight:700;
  text-decoration:none;
  display:inline-flex;
  align-items:center;
  transition: all 0.2s;
}
.btn-sm:hover{ background:#F3F0FF; }

.gal-search input{
  height:42px; padding:10px 14px; border-radius:10px;
  border:1px solid ${brand.neutralBorder};
  outline:none; min-width: 260px;
}
.gal-search input:focus{ border-color:${brand.purple}; box-shadow:0 0 0 3px ${brand.purpleTint}; }

.gal-sort label{ display:flex; gap:8px; align-items:center; font-size:14px; font-weight:700; }
.gal-sort select{
  height:42px; border-radius:10px; border:1px solid ${brand.neutralBorder};
  padding:8px 12px; font-weight:600; min-width: 240px;
}
.gal-sort select:focus{ border-color:${brand.purple}; box-shadow:0 0 0 3px ${brand.purpleTint}; outline:none; }

.masonry{ column-count: 1; column-gap: 16px; }
@media (min-width: 640px){ .masonry{ column-count: 2; } }
@media (min-width: 980px){ .masonry{ column-count: 3; } }
@media (min-width: 1280px){ .masonry{ column-count: 4; } }

.masonry-card{
  break-inside: avoid;
  background: #fff;
  border-radius:14px;
  border:1px solid ${brand.neutralBorder};
  margin: 0 0 16px;
  overflow:hidden;
  box-shadow:${brand.shadow};
  cursor:pointer;
  transition: transform .15s ease;
}
.masonry-card:hover{ transform: translateY(-2px); }

.img-wrap{ position:relative; overflow:hidden; }

.img-wrap img{
  position:relative;
  z-index:1;
  width:100%; height:auto; display:block;
  transform: translateZ(0);
  filter: blur(16px);
  opacity: .6;
  transition: filter .35s ease, opacity .35s ease;
}
.img-wrap img.ready{ filter: blur(0); opacity: 1; }

.style-chip{
  position:absolute; top:10px; left:10px;
  z-index:5;
  background:${brand.purple};
  color:#fff; font-weight:800; font-size:12px;
  padding:6px 10px; border-radius:999px; box-shadow:${brand.shadow};
}

.home-chip{
  position:absolute; top:10px; right:10px;
  z-index:5;
  background:#fff; color:${brand.purple}; font-weight:800; font-size:11px;
  padding:6px 10px; border-radius:999px; border:1px solid ${brand.purpleBorder};
  box-shadow:${brand.shadow};
}
.video-chip{
  position:absolute; top:10px; left:10px;
  z-index:5;
  background:rgba(0,0,0,.7); color:#fff; font-size:13px;
  padding:4px 8px; border-radius:999px;
  box-shadow:${brand.shadow};
}

.hover-bar{
  position:absolute; inset:auto 10px 10px 10px;
  z-index:6;
  display:flex; gap:8px; justify-content:flex-end;
  opacity:0; transform: translateY(6px);
  transition: all .18s ease;
}
.img-wrap:hover .hover-bar{ opacity:1; transform: translateY(0); }

.action{
  background:#fff; padding:6px 10px; border-radius:10px;
  border:1px solid ${brand.neutralBorder}; font-weight:600; font-size:13px;
  display:inline-flex; align-items:center; gap:6px; height:32px;
}
.action.icon{ padding:6px; width:32px; justify-content:center; }
.action.on{ border-color:${brand.purple}; color:${brand.purple}; background:${brand.purpleTint}; }
.action.danger{ border-color:#FECACA; }

.meta{ padding:10px 12px 12px; display:grid; gap:6px; }
.prompt{ font-weight:600; font-size:14px; color:#111827; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.stamp{ font-size:12px; color:#6B7280; }

.load-more{ display:flex; justify-content:center; margin: 8px 0 24px; }

.lb-backdrop{
  position:fixed; inset:0; background:rgba(17,24,39,.6);
  display:grid; place-items:center; z-index:60;
}
.lb-dialog{
  background:#fff; border-radius:16px; overflow:hidden; max-width: min(1080px, 92vw);
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
  display:grid; grid-template-columns: 1fr 360px; gap:0;
}
@media (max-width: 900px){
  .lb-dialog{ grid-template-columns: 1fr; max-height: 90vh; overflow:auto; }
}
.lb-dialog img{ width:100%; height:100%; object-fit:contain; background:#000; }
.lb-info{ padding:16px; display:grid; gap:12px; }
.lb-row{ display:flex; justify-content:space-between; align-items:center; }

.lb-style{
  background:${brand.purpleTint};
  color:${brand.purple};
  border:1px solid ${brand.purpleBorder};
  padding:6px 10px;
  border-radius:999px;
  font-weight:800;
  font-size:12px;
}

.lb-date{ color:#6B7280; font-size:12px; }
.lb-prompt{ font-weight:600; }
.lb-animate{ border-top:1px solid var(--hairline, #e5e7eb); padding-top:12px; display:grid; gap:6px; }
.lb-anim-hint{ color:#6B7280; font-size:12px; }
.lb-anim-note{ color:#6B7280; font-size:12px; margin-top:6px; }
.lb-actions{ display:flex; gap:10px; margin-top:8px; flex-wrap:wrap; }
.btn{
  height:40px; padding:0 14px; border-radius:12px; border:1px solid ${brand.neutralBorder}; background:#fff; font-weight:700;
}
.btn-primary{
  background:${brand.purple}; color:#fff; border-color:${brand.purple};
}
.btn.danger{
  border-color:#FECACA;
  background:#FEF2F2;
  color:#B91C1C;
}

.sk-img{ height: 220px; background: linear-gradient(90deg,#f5f5f7, #ececf2, #f5f5f7); animation: sk 1.2s infinite; }
.sk-line{ height:12px; margin-top:8px; background:#EEE; border-radius:8px; }
.sk-line.short{ width: 40%; }
@keyframes sk { 0%{background-position:-200px 0} 100%{background-position:calc(200px + 100%) 0} }

.empty{ text-align:center; padding: 64px 16px; border:1px dashed ${brand.neutralBorder}; border-radius:14px; }
.empty-actions{ display:flex; justify-content:center; gap:12px; margin-top:10px; flex-wrap:wrap; }
    `}</style>
  );
}